import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { learningApi } from "../api/learningApi";
import toast from "react-hot-toast";
import { getErrorMessage } from "@/lib/api";

export function useChapter(chapterId: string) {
  return useQuery({
    queryKey: ["chapter", chapterId],
    queryFn: () => learningApi.getChapter(chapterId),
    enabled: !!chapterId,
  });
}

export function useCourseChapters(courseId: string) {
  return useQuery({
    queryKey: ["course-chapters", courseId],
    queryFn: () => learningApi.getChaptersByCourse(courseId),
    enabled: !!courseId,
  });
}

export function useMarkChapterComplete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chapterId: string) =>
      learningApi.markChapterComplete(chapterId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["chapter"] });
      queryClient.invalidateQueries({ queryKey: ["course-chapters"] });
      queryClient.invalidateQueries({ queryKey: ["course"] });
      queryClient.invalidateQueries({ queryKey: ["enrollments"] });
      queryClient.invalidateQueries({ queryKey: ["my-courses"] });
      queryClient.invalidateQueries({ queryKey: ["student-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["gamification-summary"] });
      queryClient.invalidateQueries({ queryKey: ["badges"] });

      // Solo mostramos toast si no hay badges (la celebración lo reemplaza)
      if (!data.newBadges?.length) {
        if (data.isCourseComplete) {
          toast.success("¡Felicidades! Has completado el curso");
        } else {
          toast.success("Capítulo completado");
        }
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useQuiz(chapterId: string) {
  return useQuery({
    queryKey: ["quiz", chapterId],
    queryFn: () => learningApi.getQuizByChapter(chapterId),
    enabled: !!chapterId,
  });
}

export function useUpdateVideoProgress() {
  return useMutation({
    mutationFn: ({
      chapterId,
      lastPosition,
      videoProgress,
    }: {
      chapterId: string;
      lastPosition: number;
      videoProgress?: number;
    }) =>
      learningApi.updateVideoProgress(chapterId, {
        lastPosition,
        videoProgress,
      }),
    // No mostramos toast ni invalidamos queries para evitar interrupciones
    // El progreso se guarda silenciosamente en segundo plano
  });
}

export function useStartQuizAttempt() {
  return useMutation({
    mutationFn: (quizId: string) => learningApi.startQuizAttempt(quizId),
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useSubmitQuizAttempt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      attemptId,
      answers,
    }: {
      attemptId: string;
      answers: Array<{ questionId: string; selectedOptionIds: string[] }>;
    }) => learningApi.submitQuizAttempt(attemptId, answers),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["quiz"] });
      queryClient.invalidateQueries({ queryKey: ["chapter"] });
      queryClient.invalidateQueries({ queryKey: ["gamification-summary"] });
      queryClient.invalidateQueries({ queryKey: ["badges"] });

      // Solo mostramos toast si no hay badges nuevos (la celebración lo reemplaza)
      if (!data.newBadges?.length) {
        if (data.passed) {
          toast.success(`Aprobado con ${data.score}%!`);
        } else {
          toast.error(
            `No aprobado. Obtuviste ${data.score}%, necesitas ${data.passingScore}%`,
          );
        }
      } else if (data.passed) {
        // Si hay badges, solo mostramos el mensaje de aprobado (el badge se celebra aparte)
        toast.success(`Aprobado con ${data.score}%!`);
      } else {
        toast.error(
          `No aprobado. Obtuviste ${data.score}%, necesitas ${data.passingScore}%`,
        );
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
